import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { RoofSection } from '../../types';
import { api } from '../../lib/api';

// Hook to fetch roof sections for a measurement
export function useRoofSections(measurementId?: number) {
  return useQuery({
    queryKey: ['roof-sections', measurementId],
    queryFn: async () => {
      const params = measurementId ? `?measurement=${measurementId}` : '';
      const response = await api.get(`/api/roof-sections/${params}`);
      // Ensure we always return an array
      const data = response.data as any;
      if (Array.isArray(data)) {
        return data as RoofSection[];
      } else if (data?.results && Array.isArray(data.results)) {
        return data.results as RoofSection[];
      }
      return [] as RoofSection[];
    },
    enabled: !!measurementId,
  });
}

// Hook to fetch all roof sections (admin use)
export function useAllRoofSections() {
  return useQuery({
    queryKey: ['roof-sections'],
    queryFn: async () => {
      const response = await api.get('/api/roof-sections/');
      // Ensure we always return an array
      const data = response.data as any;
      if (Array.isArray(data)) {
        return data as RoofSection[];
      } else if (data?.results && Array.isArray(data.results)) {
        return data.results as RoofSection[];
      }
      return [] as RoofSection[];
    },
  });
}

// Hook to fetch a single roof section
export function useRoofSection(id: number) {
  return useQuery({
    queryKey: ['roof-section', id],
    queryFn: async () => {
      const response = await api.get(`/api/roof-sections/${id}/`);
      return response.data as RoofSection;
    },
    enabled: !!id,
  });
}

// Hook to create a roof section
export function useCreateRoofSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Omit<RoofSection, 'id' | 'created_at' | 'updated_at' | 'points' | 'total_points'>) => {
      const response = await api.post('/api/roof-sections/', data);
      return response.data as RoofSection;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['roof-sections'] });
      queryClient.invalidateQueries({ queryKey: ['roof-sections', data.measurement] });
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
      queryClient.invalidateQueries({ queryKey: ['measurement', data.measurement] });
    },
  });
}

// Hook to update a roof section
export function useUpdateRoofSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<RoofSection> }) => {
      const response = await api.patch(`/api/roof-sections/${id}/`, data);
      return response.data as RoofSection;
    },
    onSuccess: (data) => {
      // Update specific roof section in cache
      queryClient.setQueryData(['roof-section', data.id], data);
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['roof-sections'] });
      queryClient.invalidateQueries({ queryKey: ['roof-sections', data.measurement] });
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
      queryClient.invalidateQueries({ queryKey: ['measurement', data.measurement] });
    },
  });
}

// Hook to delete a roof section
export function useDeleteRoofSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/roof-sections/${id}/`);
      return id;
    },
    onSuccess: (deletedId, variables) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['roof-section', deletedId] });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['roof-sections'] });
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
    },
  });
}

// Hook to calculate area for a roof section
export function useCalculateRoofArea() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (roofSectionId: number) => {
      const response = await api.post(`/api/roof-sections/${roofSectionId}/calculate_area/`, {});
      return response.data;
    },
    onSuccess: (data, roofSectionId) => {
      // Refresh the specific roof section
      queryClient.invalidateQueries({ queryKey: ['roof-section', roofSectionId] });
      queryClient.invalidateQueries({ queryKey: ['roof-sections'] });
    },
  });
}

// Utility hook to create multiple roof sections at once
export function useBulkCreateRoofSections() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (sections: Array<Omit<RoofSection, 'id' | 'created_at' | 'updated_at' | 'points' | 'total_points'>>) => {
      const responses = await Promise.all(
        sections.map(section => api.post('/api/roof-sections/', section))
      );
      return responses.map(response => response.data as RoofSection);
    },
    onSuccess: (data) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['roof-sections'] });
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
      
      // If all sections belong to the same measurement, invalidate that specific query
      if (data.length > 0) {
        const measurementId = data[0].measurement;
        if (data.every(section => section.measurement === measurementId)) {
          queryClient.invalidateQueries({ queryKey: ['roof-sections', measurementId] });
          queryClient.invalidateQueries({ queryKey: ['measurement', measurementId] });
        }
      }
    },
  });
}